import React, { useEffect, useRef } from 'react'

import { FormikProps, useFormikContext } from 'formik'

import {
  BoxV4,
  DatePickerV2,
  PlanType,
  TextAreaV4,
  TextInputV4,
} from '@labrav/react-components'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty } from 'lodash'

import { useStyles } from './OfferDetails.styles'
import { CombinedFormState } from '../../../../modules/combinedForm/reducer'
import { HelpDrawer } from '@labrav/common'
import { ScheduleType } from '../../../../modules/privateOffer/reducer'

export type PrivateOfferDetailsSectionProps = {
  productType?: PlanType | undefined
  disabled: boolean
}
export const labraUIDateFormatForm = 'MMM dd, yyyy'

export const OfferDetails: React.FC<PrivateOfferDetailsSectionProps> = ({
  productType,
  disabled,
}) => {
  const formikProps = useFormikContext() as FormikProps<CombinedFormState>
  const { values, errors, touched, handleBlur, setFieldValue } = formikProps
  const isFirstRender = useRef(true)

  const privateOfferExpirationDateField = {
    title: 'Offer expiration date',
    humanReadableKey: 'Offer expiration date',
    placeHolder: 'Offer expiration date',
    id: 'cppoData.offerExpirationDate',
    isRequired: false,
    isReadOnly: disabled,
    isCustom: false,
    dataType: 'date',
    minDate: 'today',
  }
  const privateOfferUsageEndDateField = {
    title: 'Usage end date',
    humanReadableKey: 'Usage end date',
    placeHolder: 'Usage end date',
    id: 'cppoData.subscriptionEndDate',
    isRequired: false,
    isReadOnly: disabled,
    isCustom: false,
    dataType: 'date',
    minDate: 'today',
  }

  // subscriptionEndDate
  const handleCompanyNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target
    setFieldValue('resellerAgreementData.companyName', value)
    setFieldValue('cppoData.companyName', value)
  }
  const handleBuyerCloudAccountId = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target
    setFieldValue('resellerAgreementData.buyerAwsAccountId', value)
    setFieldValue('cppoData.buyerCloudAccountId', value)
  }
  const handleOfferName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setFieldValue('cppoData.offerName', value)
    setFieldValue('resellerAgreementData.agreementName', value)
  }

  const handleOfferDescriptionName = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target
    setFieldValue('cppoData.offerDescription', value)
    setFieldValue('resellerAgreementData.agreementDescription', value)
  }
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setFieldValue(
      'resellerAgreementData.agreementExpirationDate',
      values.cppoData.offerExpirationDate
    )
    setFieldValue('resellerAgreementData.installmentInfo', [])
    setFieldValue('cppoData.installmentInfo', [])
    setFieldValue('cppoData.flexiblePaymentType', ScheduleType.CUSTOM)
    setFieldValue('resellerAgreementData.firstInstallmentDate', '')
  }, [values.cppoData.offerExpirationDate])

  const { classes } = useStyles()
  return (
    <div data-testid="details-section">
      <BoxV4
        heading={'Offer details'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
        secondaryActions={
          <HelpDrawer
            sectionName="combinedFormOfferDetails"
            headerTitle="Help & FAQs"
          />
        }
      >
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            {
              // coppy in cppo
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="resellerAgreementData.companyName"
                  title="Buyer name"
                  type="text"
                  placeHolder=""
                  value={values.resellerAgreementData.companyName as string}
                  updatedValue={
                    values.resellerAgreementData.companyName as string
                  }
                  maxNumber={72}
                  isError={
                    !!(
                      get(
                        touched,
                        'resellerAgreementData.companyName',
                        false
                      ) &&
                      get(errors, 'resellerAgreementData.companyName', false)
                    )
                  }
                  onChange={handleCompanyNameChange}
                  isReadOnly={disabled}
                  isRequired={false}
                  errorText={
                    (!!get(
                      touched,
                      'resellerAgreementData.companyName',
                      false
                    ) &&
                      get(errors, 'resellerAgreementData.companyName', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            }

            {/*copy in cppo */}
            <div className={classes.inputWidth}>
              <TextInputV4
                keyName="resellerAgreementData.buyerAwsAccountId"
                title="Buyer AWS account ID"
                type="text"
                placeHolder=""
                value={
                  (values.resellerAgreementData.buyerAwsAccountId ||
                    '') as string
                }
                updatedValue={
                  (values.resellerAgreementData.buyerAwsAccountId ||
                    '') as string
                }
                maxNumber={12}
                isError={
                  !!(
                    get(
                      touched,
                      'resellerAgreementData.buyerAwsAccountId',
                      false
                    ) &&
                    get(
                      errors,
                      'resellerAgreementData.buyerAwsAccountId',
                      false
                    )
                  )
                }
                onChange={handleBuyerCloudAccountId}
                isReadOnly={disabled}
                isRequired={false}
                errorText={
                  (!!get(
                    touched,
                    'resellerAgreementData.buyerAwsAccountId',
                    false
                  ) &&
                    get(
                      errors,
                      'resellerAgreementData.buyerAwsAccountId',
                      ' '
                    )) ||
                  ' '
                }
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
          </div>
        </div>
        {/* coppy in reller aggrementName */}
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            <div className={classes.inputWidth}>
              <TextInputV4
                keyName="cppoData.offerName"
                title="Offer name"
                type="text"
                placeHolder=""
                value={values.cppoData.offerName}
                updatedValue={values.cppoData.offerName}
                maxNumber={72}
                isError={
                  !!(
                    get(touched, 'cppoData.offerName', false) &&
                    get(errors, 'cppoData.offerName', false)
                  )
                }
                onChange={handleOfferName}
                isReadOnly={disabled}
                isRequired={false}
                errorText={
                  (!!get(touched, 'cppoData.offerName', false) &&
                    get(errors, 'cppoData.offerName', ' ')) ||
                  ' '
                }
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>

            <div className={classes.inputWidth}>
              <DatePickerV2
                format={labraUIDateFormatForm}
                views={['year', 'month', 'day']}
                field={privateOfferExpirationDateField}
                key={'cppoData.offerExpirationDate'}
                showErrorHelper={
                  !!touched.cppoData?.offerExpirationDate &&
                  !isEmpty(errors.cppoData?.offerExpirationDate)
                }
                fieldId={'cppoData.offerExpirationDate'}
                formik={
                  formikProps as unknown as FormikProps<Record<string, unknown>>
                }
                errorHelperText={
                  (!!touched.cppoData?.offerExpirationDate &&
                    errors.cppoData?.offerExpirationDate) ||
                  ''
                }
                size="small"
              />
            </div>
          </div>
        </div>

        {productType === PlanType.SAAS_SUBSCRIPTION && (
          <div className={classes.sectionContainer}>
            <div className={classes.companyDetailsContainer}>
              <div className={classes.inputWidth}>
                <DatePickerV2
                  format={labraUIDateFormatForm}
                  field={privateOfferUsageEndDateField}
                  key={'cppoData.subscriptionEndDate'}
                  showErrorHelper={
                    !!touched.cppoData?.subscriptionEndDate &&
                    !isEmpty(errors.cppoData?.subscriptionEndDate)
                  }
                  fieldId={'cppoData.subscriptionEndDate'}
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  errorHelperText={
                    (!!touched.cppoData?.subscriptionEndDate &&
                      errors.cppoData?.subscriptionEndDate) ||
                    ''
                  }
                  views={['year', 'month', 'day']}
                  size="small"
                />
              </div>
              <div className={classes.inputWidth}></div>
            </div>
          </div>
        )}

        {/* copy in resale agrement description */}
        <div className={classes.sectionContainer}>
          <TextAreaV4
            keyName="cppoData.offerDescription"
            title="Offer description (Optional)"
            rows={10}
            maxNumber={1000}
            height={'66px'}
            type="string"
            placeHolder="Offer description (Optional)"
            value={
              (values.cppoData.offerDescription === '-'
                ? ''
                : values.cppoData.offerDescription || '') as string
            }
            updatedValue={
              (values.cppoData.offerDescription === '-'
                ? ''
                : values.cppoData.offerDescription || '') as string
            }
            isError={
              !!(
                errors.cppoData?.offerDescription &&
                touched.cppoData?.offerDescription
              )
            }
            onChange={handleOfferDescriptionName}
            isReadOnly={disabled}
            isRequired={false}
            errorText={
              (!!touched.cppoData?.offerDescription &&
                errors.cppoData?.offerDescription) ||
              ''
            }
            showSpinner={false}
            onBlur={handleBlur}
          />
        </div>
      </BoxV4>
    </div>
  )
}
